import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMarkBoletoAsPaid } from './useBoletos';
import type { BillingInvoice } from './types';

function parseBrl(s: string): number {
  const cleaned = s
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boleto: BillingInvoice | null;
}

export function MarkAsPaidDialog({ open, onOpenChange, boleto }: Props) {
  const markPaid = useMarkBoletoAsPaid();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [paidAt, setPaidAt] = useState(today);
  const [paidAmountStr, setPaidAmountStr] = useState('');

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open || !boleto) return;
    setPaidAt(today);
    setPaidAmountStr(Number(boleto.amount).toFixed(2).replace('.', ','));
  }, [open, boleto, today]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (!boleto) return null;

  const paidAmount = parseBrl(paidAmountStr);
  const valid = paidAmount > 0 && !!paidAt;
  const diff = paidAmount - Number(boleto.amount);

  const handleSubmit = async () => {
    if (!valid) return;
    try {
      await markPaid.mutateAsync({
        boleto,
        paidAt: new Date(`${paidAt}T12:00:00`).toISOString(),
        paidAmount,
      });
      onOpenChange(false);
    } catch {
      // toast já mostrou
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">Marcar boleto como pago</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm">
            <p className="font-semibold">{boleto.payee_name}</p>
            <p className="text-muted-foreground">{boleto.description}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Valor original: <strong>{formatBrl(Number(boleto.amount))}</strong>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="paid-date">Data do pagamento</Label>
              <Input
                id="paid-date"
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="paid-amount">Valor pago</Label>
              <Input
                id="paid-amount"
                placeholder="R$ 0,00"
                value={paidAmountStr}
                onChange={(e) => setPaidAmountStr(e.target.value)}
              />
            </div>
          </div>

          {Math.abs(diff) > 0.001 && (
            <div
              className={`rounded border p-2 text-xs ${
                diff > 0
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-blue-200 bg-blue-50 text-blue-800'
              }`}
            >
              {diff > 0 ? (
                <>
                  Pagamento <strong>{formatBrl(diff)}</strong> acima do boleto (juros/multa).
                </>
              ) : (
                <>
                  Pagamento <strong>{formatBrl(Math.abs(diff))}</strong> abaixo do boleto
                  (desconto/abatimento).
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={markPaid.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || markPaid.isPending} variant="income">
            {markPaid.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Confirmando…
              </>
            ) : (
              <>
                <CheckCircle2 size={14} className="mr-2" />
                Confirmar pagamento
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
